/** Home page videos — one source, edited by hand. Numbers as of 27 Oct 2026. */
import { SOCIAL } from "@/lib/social";

export type VideoKind = "long" | "short";
export type HomeVideo = {
  id: string;
  title: string;
  kind: VideoKind;
  views: number;
  duration?: string;
  /** "r, g, b" for the colour wash over the thumbnail. */
  tint: string;
};

export const VIDEO_TINTS = ["155, 72, 63", "53, 93, 107", "90, 42, 36", "138, 79, 47", "129, 60, 52"] as const;

let n = 0;
const v = (id: string, title: string, kind: VideoKind, views: number, duration?: string): HomeVideo => ({
  id,
  title,
  kind,
  views,
  duration,
  tint: VIDEO_TINTS[n++ % VIDEO_TINTS.length],
});

export const CHANNEL = { subscribers: 314000, videos: 363, url: SOCIAL.youtube };

export const FEATURED_VIDEO = v("Uyir3YaqiII", "أخطر شخص في حياة طفلك قد يكون أمامك كل يوم!", "long", 3800, "10:29");

export const LONG_VIDEOS: HomeVideo[] = [
  v("ygBh0rMLVww", "دماغ طفلك هو المفتاح لسلوكه!", "long", 21000, "4:21"),
  v("5IeWBCGOmiY", "حل سحري لنوبات الغضب عند الأطفال!", "long", 10000, "2:41"),
  v("KhR8PFv3SkA", "كيف نُحضّر الطفل للذهاب إلى المدرسة لأول مرة", "long", 9500, "22:25"),
];

export const TOP_SHORTS: HomeVideo[] = [
  v("DlBf0zGHv6U", "لا تخلي الخوف من الاتساخ يمنع طفلك من اللعب!", "short", 9200000),
  v("dnaclJYMmMQ", "طفلك ما بحب أخته!", "short", 8800000),
  v("KfIfoZO9hxI", "صناعة طفل يكذب!", "short", 4800000),
  v("iQbeeM-5vX4", "طفلي ثقته بنفسه معدومة!", "short", 3000000),
  v("Sqiqg5LqyAM", "كيف خلي طفلي يعرف قيمته بدون غرور؟", "short", 2300000),
];

export const INTRO_SHORT = v("9CMQ6xf17S4", "من تطوير نفسي إلى دكتورة! كيف غيرت حياتي؟", "short", 58000);
export const BOUSALA_SHORT = v("zSdvEaVfBsM", "اختبار البوصلة!", "short", 1200000);

/** Keyed by HOME_SITUATIONS slug. */
export const SITUATION_SHORTS: Record<string, HomeVideo> = {
  "tifli-yadribni": v("-G89Dk-ZjtU", "كيف أتعامل مع ضرب طفلي أمام الناس؟", "short", 1800000),
  "nawm-al-tifl": v("WeJe9h5-rm4", "الفِراق والفَقد عند الطفل", "short", 790000),
  "al-shashat-wa-dimagh-al-tifl": v("QIRpTdsiYm8", "لماذا طفلي لا يأكل إلا على الشاشة!", "short", 259000),
  "ibni-yadrib-akhah": v("dnaclJYMmMQ", "طفلك ما بحب أخته!", "short", 8800000),
  "al-tifl-al-anid": v("bIXWnXNR6D0", "كيف تخلي طفلك يسمع كلامك!", "short", 127000),
};

/** The most-watched short (hero card + figures tile). */
export const TOP_SHORT = TOP_SHORTS[0];

const oneDecimal = (x: number) => {
  const r = Math.round(x * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
};

/** 9200000 → «9.2 مليون», 127000 → «127 ألف», 3800 → «3.8 ألف». Latin digits only. */
export function formatViewsAr(n: number): string {
  if (n >= 1_000_000) return `${oneDecimal(n / 1_000_000)} مليون`;
  if (n >= 10_000) return `${Math.round(n / 1000)} ألف`;
  if (n >= 1_000) return `${oneDecimal(n / 1000)} ألف`;
  return String(n);
}

/** "9.2M" style for the figures tile. */
export function formatViewsShortLatin(n: number): string {
  return n >= 1_000_000 ? `${oneDecimal(n / 1_000_000)}M` : `${Math.round(n / 1000)}K`;
}

export function thumbUrl(v: HomeVideo, fallback = false): string {
  if (v.kind === "long") return `https://i.ytimg.com/vi/${v.id}/hq720.jpg`;
  return `https://i.ytimg.com/vi/${v.id}/${fallback ? "hqdefault" : "oardefault"}.jpg`;
}

/** YouTube answers a missing oardefault/hq720 with a 120x90 grey image instead of an error: swap to hqdefault. */
export function onThumbLoad(e: { currentTarget: HTMLImageElement }) {
  const img = e.currentTarget;
  if (img.naturalWidth > 120 || img.src.includes("/hqdefault.jpg")) return;
  img.src = img.src.replace(/\/(oardefault|hq720)\.jpg$/, "/hqdefault.jpg");
}

/** Same check for images that finished loading before hydration attached onLoad. */
export function thumbRef(img: HTMLImageElement | null) {
  if (img?.complete && img.naturalWidth > 0) onThumbLoad({ currentTarget: img });
}
